/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ApprovalStatusLabel } from '../../../../../../../api/enumerations';
import { getOperationFlow } from '../../../../../../../api/theHive/licensedCompanies';
import { OperationFlowItem } from '../../../../../../../api/theHive/licensedCompanies/types';
import { Constants } from '../../../../../../../constants/hiveNotices';
import {
  IconCloseMS,
  IconSearchMS,
} from '../../../../../../../constants/icons';
import { formatCnpjCpf, formatDate } from '../../../../../../../helpers';
import { useDebounce } from '../../../../../../../hooks/useDebounce';
import useErrorMessage from '../../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../../hooks/useSnackbar';
import { CommentsCell } from '../../../components/CompanyComments/CommentsCell';
import { CommentsDrawer } from '../../../components/CompanyComments/CommentsDrawer';
import {
  ClearSearchButton,
  SearchInput,
  StyledCell,
  StyledPagination,
  StyledRow,
  TableCellHeader,
} from '../styles';
import { AnalysisStatusIcon } from './AnalysisStatusIcon';

const PER_PAGE = 100;

export function OperationFlow(): JSX.Element {
  const [items, setItems] = useState<OperationFlowItem[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [commentsTarget, setCommentsTarget] =
    useState<OperationFlowItem | null>(null);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();
  const navigate = useNavigate();
  const { notice: noticeIdParam } = useParams();
  const noticeId = parseInt(noticeIdParam as string, 10);

  // Page and search live in the URL to survive returning from the analysis.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const searchFromUrl = searchParams.get('search') || '';

  const [searchTerm, setSearchTerm] = useState(searchFromUrl);
  const debouncedSearch = useDebounce(searchTerm);

  const applySearchToUrl = (value: string): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set('search', value);
        else next.delete('search');
        next.delete('page');
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (debouncedSearch !== searchFromUrl) applySearchToUrl(debouncedSearch);
  }, [debouncedSearch]);

  const handleClearSearch = (): void => {
    setSearchTerm('');
    applySearchToUrl('');
  };

  const handleNavigate = (companyId: number): void => {
    navigate(`./request/${companyId}`);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(value));
      return next;
    });
  };

  const fetchOperationFlow = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await getOperationFlow(
        noticeId,
        page,
        PER_PAGE,
        [
          ApprovalStatusLabel.PENDING,
          ApprovalStatusLabel.AWAITING_FORMALIZATION,
          ApprovalStatusLabel.FORMALIZATION_SUBMITTED,
        ],
        searchFromUrl
      );
      if (response instanceof Error) throw response;
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      setItems(response.data ?? []);
      setTotalPages(response.detail.total_pages ?? 1);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoading(false);
    }
  }, [noticeId, page, searchFromUrl]);

  useEffect(() => {
    if (!Number.isNaN(noticeId)) fetchOperationFlow();
  }, [fetchOperationFlow]);

  // Nothing to search with zero requests; stays enabled while a search is
  // active so the filter can be cleared.
  const searchDisabled = !searchFromUrl && !loading && items.length === 0;

  const renderRows = (): JSX.Element => {
    if (loading) {
      return (
        <StyledRow>
          <StyledCell colSpan={7} align="center">
            <CircularProgress size={24} />
          </StyledCell>
        </StyledRow>
      );
    }

    if (items.length === 0) {
      return (
        <StyledRow>
          <StyledCell colSpan={7} align="center">
            {searchFromUrl
              ? Constants.noRequestsFound
              : Constants.noRequestsRegistered}
          </StyledCell>
        </StyledRow>
      );
    }

    return (
      <>
        {items.map((item) => (
          <StyledRow
            key={item.notice_company_id}
            onClick={() => handleNavigate(item.notice_company_id)}
          >
            <StyledCell>{item.company_name}</StyledCell>
            <StyledCell align="center">{formatCnpjCpf(item.cnpj)}</StyledCell>
            <StyledCell align="center">
              {item.requested_at ? formatDate(item.requested_at) : '—'}
            </StyledCell>
            <StyledCell align="center">
              <AnalysisStatusIcon status={item.juridico_status} />
            </StyledCell>
            <StyledCell align="center">
              <AnalysisStatusIcon status={item.engenharia_status} />
            </StyledCell>
            <StyledCell align="center">
              <AnalysisStatusIcon
                status={item.administrativo_status ?? 'na'}
                axis="contract"
                submittedAt={item.formalization_submitted_at}
              />
            </StyledCell>
            <StyledCell align="center">
              <CommentsCell
                count={item.comments_count ?? 0}
                disabled={!item.licensed_company_id}
                onOpen={() => setCommentsTarget(item)}
              />
            </StyledCell>
          </StyledRow>
        ))}
      </>
    );
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <SearchInput
          placeholder={Constants.searchCompany}
          inputProps={{ 'aria-label': 'pesquisar', maxLength: 60 }}
          value={searchTerm}
          disabled={searchDisabled}
          onChange={(e) => setSearchTerm(e.target.value)}
          startAdornment={
            <InputAdornment position="start">{IconSearchMS}</InputAdornment>
          }
          endAdornment={
            searchTerm ? (
              <InputAdornment position="end">
                <ClearSearchButton
                  aria-label={Constants.clearSearch}
                  onClick={handleClearSearch}
                >
                  {IconCloseMS}
                </ClearSearchButton>
              </InputAdornment>
            ) : null
          }
        />
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCellHeader width="24%">Nome da empresa</TableCellHeader>
              <TableCellHeader align="center" width="18%">
                CNPJ
              </TableCellHeader>
              <TableCellHeader align="center" width="18%">
                Data do pedido
              </TableCellHeader>
              <TableCellHeader align="center" width="11%">
                Jurídico
              </TableCellHeader>
              <TableCellHeader align="center" width="11%">
                Engenharia
              </TableCellHeader>
              <TableCellHeader align="center" width="12%">
                Administrativo
              </TableCellHeader>
              <TableCellHeader align="center" width="7%">
                Notas
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>{renderRows()}</TableBody>
        </Table>
      </TableContainer>
      <StyledPagination
        page={page}
        count={totalPages}
        onChange={handlePageChange}
      />
      <CommentsDrawer
        open={!!commentsTarget}
        onClose={() => setCommentsTarget(null)}
        noticeId={noticeId}
        licensedCompanyId={commentsTarget?.licensed_company_id}
        companyName={commentsTarget?.company_name ?? ''}
      />
    </>
  );
}
